import type { FC } from 'react';
import { Fragment } from 'react';
import { Box, Grid, Stack } from '@mui/material';
import type { TFunction } from 'i18next';

import type { Filter, InspirationValue } from 'src/types/followUpInspirations';
import { KPI_DISPLAY_CUTOFF } from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/constants';

import { styles } from './index.styles';

export const KPI_POINTS_WIDGET_TEST_ID = 'kpi-points-widget';
export const KPI_POINTS_WIDGET_VALUE_TEST_ID = 'kpi-points-value-widget';

interface KpiPointsWidgetProps {
  inspirationValues: InspirationValue[];
  highlightedKpi?: Filter;
  t: TFunction;
}

const KpiPointsWidget: FC<KpiPointsWidgetProps> = ({
  inspirationValues,
  highlightedKpi,
  t,
}) => (
  <>
    <Grid container sx={styles.header}>
      <Grid xs={4.5} item>
        KPI
      </Grid>
      <Grid xs={7.5} item>
        {t('followUpInspirations:kpiPointsWidgetHeadline')}
      </Grid>
    </Grid>
    <Box
      data-test-id={KPI_POINTS_WIDGET_TEST_ID}
      sx={{
        flexGrow: 1,
      }}
    >
      <Grid container spacing={2}>
        {inspirationValues
          .sort((inspirationValue1, inspirationValue2) =>
            inspirationValue1.value || inspirationValue2.value
              ? inspirationValue2.value - inspirationValue1.value
              : inspirationValue1.kpi.label.localeCompare(
                  inspirationValue2.kpi.label,
                ),
          )
          .reduce(
            (acc: Array<InspirationValue>, inspirationValue) =>
              inspirationValue.kpi.id === highlightedKpi?.id
                ? [inspirationValue, ...acc]
                : [...acc, inspirationValue],
            [],
          )
          .map((inspirationValue, i) => (
            <Fragment key={i}>
              <Grid xs={4.5} item>
                <Box
                  sx={[
                    styles.kpiLabel,
                    !!inspirationValue.value &&
                      (i === 0 ? styles.extraHighlighted : styles.highlighted),
                  ]}
                >
                  {inspirationValue.kpi.label}
                </Box>
              </Grid>
              <Grid xs={7.5} item>
                {!!inspirationValue.value && (
                  <Stack
                    direction="row"
                    sx={styles.kpiBar(
                      (inspirationValue.value <= KPI_DISPLAY_CUTOFF
                        ? inspirationValue.value
                        : KPI_DISPLAY_CUTOFF) *
                        (100 / KPI_DISPLAY_CUTOFF),
                    )}
                    data-test-id={KPI_POINTS_WIDGET_VALUE_TEST_ID}
                  >
                    <Box sx={styles.arrow}>
                      <Box sx={styles.dottedLine} />
                      <Box sx={styles.arrowHead} />
                    </Box>
                    <Box
                      sx={[
                        styles.bubble(i),
                        i === 0 ? styles.extraHighlighted : styles.highlighted,
                      ]}
                    >
                      {inspirationValue.value >= KPI_DISPLAY_CUTOFF
                        ? `≥ ${KPI_DISPLAY_CUTOFF}`
                        : `+ ${Number(inspirationValue.value).toFixed(1)}`}
                    </Box>
                  </Stack>
                )}
              </Grid>
            </Fragment>
          ))}
      </Grid>
    </Box>
  </>
);

export default KpiPointsWidget;
